import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store';
import CommonHeader from '../components/layout/CommonHeader';
import UserListItem from '../components/common/UserListItem';
import { useGetUserFollowers, useToggleFollow } from '../features/user';

const Followers: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [followingStates, setFollowingStates] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [allFollowers, setAllFollowers] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingUserIds, setLoadingUserIds] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Use current user's ID if no userId in params (viewing own followers)
  const targetUserId = userId || currentUser?.id;
  
  // Fetch followers data
  const { data, loading, error, fetchMore, networkStatus } = useGetUserFollowers(targetUserId || '', 20);
  const [toggleFollow] = useToggleFollow();

  // Set document title
  useEffect(() => {
    document.title = 'Followers - zapOdeon';
  }, []);

  // Initialize followers when data loads
  useEffect(() => {
    if (data?.userFollowers && currentPage === 1) {
      setAllFollowers(data.userFollowers);
      setHasMore(data.userFollowers.length === 20); // If we got less than 20, no more items
      
      // Initialize following states
      const states: Record<string, boolean> = {};
      data.userFollowers.forEach((follower: any) => {
        states[follower.id] = follower.is_followed;
      });
      setFollowingStates(states);
    }
  }, [data, currentPage]);

  // Load more followers
  const loadMore = useCallback(async () => {
    if (!hasMore || loading || networkStatus === 1) return;

    // Save current scroll position before loading
    const container = scrollContainerRef.current;
    const scrollTopBefore = container?.scrollTop || 0;
    const scrollHeightBefore = container?.scrollHeight || 0;

    try {
      const result = await fetchMore({
        variables: {
          userId: targetUserId,
          first: 20,
          page: currentPage + 1
        }
      });

      if (result.data?.userFollowers) {
        const newFollowers = result.data.userFollowers;
        
        if (newFollowers.length > 0) {
          setAllFollowers(prev => [...prev, ...newFollowers]);
          setCurrentPage(prev => prev + 1);
          setHasMore(newFollowers.length === 20);
          
          // Update following states for new followers
          const newStates: Record<string, boolean> = {};
          newFollowers.forEach((follower: any) => {
            newStates[follower.id] = follower.is_followed;
          });
          setFollowingStates(prev => ({ ...prev, ...newStates }));

          // Restore scroll position after DOM update
          setTimeout(() => {
            if (container) {
              const scrollHeightAfter = container.scrollHeight;
              const scrollHeightDiff = scrollHeightAfter - scrollHeightBefore;
              container.scrollTop = scrollTopBefore + scrollHeightDiff;
            }
          }, 0);
        } else {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.error('Error loading more followers:', error);
    }
  }, [hasMore, loading, networkStatus, fetchMore, targetUserId, currentPage]);

  // Scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when scrolled to 80% of the content
    if (scrollPercentage > 0.8) {
      loadMore();
    }
  }, [loadMore]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleToggleFollow = async (userId: string) => {
    if (loadingUserIds.has(userId)) return;
    
    // Set loading state for this specific user
    setLoadingUserIds(prev => new Set([...prev, userId]));
    
    // Optimistic update
    setFollowingStates(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));

    try {
      await toggleFollow({
        variables: { userId }
      });
    } catch (error) {
      // Revert on error
      setFollowingStates(prev => ({
        ...prev,
        [userId]: !prev[userId]
      }));
      console.error('Toggle follow error:', error);
    } finally {
      // Remove loading state for this user
      setLoadingUserIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!targetUserId) {
    return (
      <div className="h-dvh bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <p>User not found</p>
        </div>
      </div>
    );
  }

  if (loading && currentPage === 1) {
    return (
      <div className="h-dvh bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading followers...</p>
        </div>
      </div>
    );
  }

  if (error && currentPage === 1) {
    return (
      <div className="h-dvh bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <p>Error loading followers</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh bg-black text-white overflow-hidden flex flex-col">
      {/* Header */}
      <CommonHeader
        title="Followers"
        onBackClick={handleBack}
      />

      {/* Scrollable Content Section */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 bg-black overflow-y-auto" 
        style={{ padding: '16px', paddingTop: '55px' }}
      >
        {allFollowers.length === 0 && !loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-white/60">
              <p>No followers yet</p>
            </div>
          </div>
        ) : (
          <div>
            {allFollowers.map((follower: any) => {
              const userWithFollowState = {
                ...follower,
                is_followed: followingStates[follower.id] ?? follower.is_followed
              };
              
              return (
                <UserListItem
                  key={follower.id}
                  user={userWithFollowState}
                  onFollowToggle={handleToggleFollow}
                  followLoading={loadingUserIds.has(follower.id)}
                  context="followers"
                />
              );
            })}
            
            {/* Loading indicator for more items */}
            {(loading && currentPage > 1) && (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Followers; 